import { BitkitPromoBanner, BitkitPromoBannerProps, rem } from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { useEffect } from 'react';

import { segmentTrack } from '@/hooks/analytics/useTracking';
import { useDismissableNotification } from '@/hooks/useDismissableNotification';

import jiraLogo from './jira-logo.svg';

const JIRA_APP_MARKETPLACE_URL = 'https://marketplace.atlassian.com/apps/1886769622/bitrise-for-jira';
const CTA_LABEL = 'Get the free Jira app';

const PROMOTION_PROPS = {
  promotion_name: 'bitrise_for_jira',
  creative_name: 'bitrise_for_jira_builds_on_issues_banner',
  creative_slot: 'app_details_page',
  platform: 'website',
};

let displayedTracked = false;

export const resetDisplayedTracking = () => {
  displayedTracked = false;
};

export const JIRA_APP_BANNER_COOKIE_NAME = 'jira_app_banner_dismissed';

export const useJiraAppBanner = () => {
  const { isHidden, dismiss } = useDismissableNotification(JIRA_APP_BANNER_COOKIE_NAME);

  return { isVisible: !isHidden, onDismiss: dismiss };
};

type JiraAppBannerProps = Omit<
  BitkitPromoBannerProps,
  'title' | 'message' | 'actionable' | 'dismissible' | 'onClose' | 'illustration'
> & {
  onDismiss: () => void;
};

const JiraAppBanner = ({ onDismiss, ...rest }: JiraAppBannerProps) => {
  useEffect(() => {
    if (!displayedTracked) {
      displayedTracked = true;
      segmentTrack('Promotion Displayed', PROMOTION_PROPS);
    }
  }, []);

  return (
    <BitkitPromoBanner
      title="See every build on its Jira issue, no tab-switching"
      message="Get the official Bitrise for Jira app from the Atlassian Marketplace and keep your team in the loop."
      illustration={
        <Box position="relative" width={rem(68)} height={rem(68)} overflow="hidden">
          <Box asChild position="absolute" insetInlineStart={rem(-7)} insetBlockStart="8">
            <img src={jiraLogo} alt="" />
          </Box>
        </Box>
      }
      actionable={{
        label: CTA_LABEL,
        href: JIRA_APP_MARKETPLACE_URL,
        target: '_blank',
        onClick: () => segmentTrack('Promotion Clicked', { ...PROMOTION_PROPS, cta_button_text: CTA_LABEL }),
      }}
      dismissible
      onClose={() => {
        segmentTrack('Promotion Dismissed', PROMOTION_PROPS);
        onDismiss();
      }}
      {...rest}
    />
  );
};

export default JiraAppBanner;
